import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogIn, LogOut, User } from 'lucide-react';
import { Youtube, Instagram, Settings, X as XIcon } from 'lucide-react';
import TikTokIcon from './icons/TikTokIcon';
import { useAuth } from '../contexts/AuthContext';
import { removeCookieConsent, setCookieConsent } from '../lib/cookieManager';

const Footer = () => {
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const { currentUser } = useAuth();
  const location = useLocation();

  const resetCookieConsent = () => {
    removeCookieConsent();
    window.location.reload();
  };

  // Fonction pour défiler vers le haut de la page
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Composant Link personnalisé qui défile vers le haut lors du clic
  const ScrollToTopLink = ({ to, children, className }) => {
    return (
      <Link
        to={to}
        className={className}
        onClick={scrollToTop}
      >
        {children}
      </Link>
    );
  };

  return (
    <footer className="bg-[#323c5f] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <img 
              src="/Logo SES.png" 
              alt="Comprendre les SES" 
              className="h-12 w-auto mb-4"
            />
            <p className="text-sm text-gray-300">
              Votre réussite en SES est notre priorité.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Nos Services</h3>
            <ul className="space-y-2">
              <li>
                <ScrollToTopLink to="/cours-particuliers" className="text-gray-300 hover:text-white transition-colors">
                  Cours Particuliers
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink to="/flashcards" className="text-gray-300 hover:text-white transition-colors">
                  Flashcards SES
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink to="/fiches-ses" className="text-gray-300 hover:text-white transition-colors">
                  Fiches SES
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink to="/sujets-bac" className="text-gray-300 hover:text-white transition-colors">
                  Sujets Bac
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink to="/ressources" className="text-gray-300 hover:text-white transition-colors">
                  Ressources Gratuites
                </ScrollToTopLink>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Légal</h3>
            <ul className="space-y-2">
              <li>
                <ScrollToTopLink to="/mentions-legales" className="text-gray-300 hover:text-white transition-colors">
                  Mentions Légales
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink to="/cgv" className="text-gray-300 hover:text-white transition-colors">
                  CGV
                </ScrollToTopLink>
              </li>
              <li>
                <ScrollToTopLink to="/confidentialite" className="text-gray-300 hover:text-white transition-colors">
                  Politique de Confidentialité
                </ScrollToTopLink>
              </li>
              <li>
                <button 
                  onClick={() => setShowCookieSettings(true)}
                  className="text-gray-300 hover:text-white transition-colors flex items-center"
                >
                  <Settings size={16} className="mr-1" />
                  Gérer les cookies
                </button>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Suivez-nous</h3>
            <div className="flex space-x-4">
              <a 
                href="https://www.youtube.com/@comprendrelesses" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white transition-colors"
              >
                <Youtube size={24} />
              </a>
              <a 
                href="https://www.instagram.com/comprendre_les_ses" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white transition-colors"
              >
                <Instagram size={24} />
              </a>
              <a 
                href="https://www.tiktok.com/@comprendre_les_ses" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white transition-colors"
              >
                <TikTokIcon size={24} />
              </a>
            </div>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-gray-700 text-center text-sm text-gray-300">
          <p>&copy; {new Date().getFullYear()} Comprendre les SES. Tous droits réservés.</p>
        </div>
      </div>

      {/* Modal de gestion des cookies */}
      {showCookieSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 text-gray-800">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-[#323c5f]">Paramètres des cookies</h3>
              <button
                onClick={() => setShowCookieSettings(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon size={24} />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-semibold text-[#323c5f]">Cookies nécessaires</h4>
                  <p className="text-sm text-gray-600">
                    Ces cookies sont indispensables au fonctionnement du site.
                  </p>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="sr-only"
                  />
                  <div className="w-10 h-5 bg-[#9a6ebd] rounded-full"></div>
                  <div className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full transform translate-x-5"></div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-semibold text-[#323c5f]">Cookies analytiques</h4>
                  <p className="text-sm text-gray-600">
                    Ces cookies nous aident à améliorer notre site.
                  </p>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    id="analytics"
                    className="sr-only peer"
                    onChange={(e) => {
                      const consent = e.target.checked;
                      setCookieConsent(consent);
                    }}
                    defaultChecked={true}
                  />
                  <div className="w-10 h-5 bg-gray-200 peer-checked:bg-[#9a6ebd] rounded-full peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#9a6ebd] peer-focus:ring-opacity-50"></div>
                  <div className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full peer-checked:transform peer-checked:translate-x-5 transition"></div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-semibold text-[#323c5f]">Cookies marketing</h4>
                  <p className="text-sm text-gray-600">
                    Ces cookies nous permettent de vous proposer des publicités pertinentes.
                  </p>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    id="marketing"
                    className="sr-only peer"
                    onChange={(e) => {
                      const consent = e.target.checked;
                      setCookieConsent(consent);
                    }}
                    defaultChecked={false}
                  />
                  <div className="w-10 h-5 bg-gray-200 peer-checked:bg-[#9a6ebd] rounded-full peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#9a6ebd] peer-focus:ring-opacity-50"></div>
                  <div className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full peer-checked:transform peer-checked:translate-x-5 transition"></div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <button
                onClick={resetCookieConsent}
                className="text-gray-700 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Réinitialiser
              </button>
              <button
                onClick={() => setShowCookieSettings(false)}
                className="bg-[#9a6ebd] text-white px-4 py-2 rounded-lg hover:bg-[#8a5dad] transition-colors"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;